<template>
  <div>
    <div class="d-flex justify-content-between align-items-center mb-4 mt-5">
      <div class="d-flex align-items-center">
        <h2>Availity</h2>
        <button @click="addClientToSite" class="btn btn-success ml-4">
          Add Client
        </button>
      </div>
      <div style="width: 50%;">
        <SearchBox
          v-model="searchTerm"
          placeholder="Search clients..."
        />
      </div>
    </div>
    <AIVTable
      v-if="filteredClients && filteredClients.length"
      :columns="clientColumns"
      :data="filteredClients"
      :pagination="{ total: 0, totalPages: 0, currentPage: 0, perPage: 0 }"
    >
      <template #cell-insurance_providers="{ row }">
        <div class="insurance-providers-column">
          <div v-if="row.insurance_providers && row.insurance_providers.length > 0">
            <span 
              v-for="(provider, index) in row.insurance_providers" 
              :key="provider.id"
              class="insurance-provider-badge"
            >
              {{ provider.canonical_name || provider.name }}{{ index < row.insurance_providers.length - 1 ? ', ' : '' }}
            </span>
          </div>
          <div v-else class="text-muted">
            No providers assigned
          </div>
        </div>
      </template>
      <template #cell-drive_location="{ row }">
        <div class="drive-location-column">
          <div v-if="row.custom_drive" class="custom-drive">
            <a :href="row.custom_drive" target="_blank" class="text-primary">
              {{ row.custom_drive }}
            </a>
          </div>
          <div v-else class="director-drive text-muted">
            Director's Drive Location
          </div>
        </div>
      </template>
      <template #cell-dates="{ row }">
        <div class="dates-column">
          <div class="created-date">Created: {{ formatDate(row.createdAt) }}</div>
          <div class="updated-date">Updated: {{ formatDate(row.updatedAt) }}</div>
        </div>
      </template>
      <template #cell-actions="{ row }">
        <button @click="editClient(row)" class="btn btn-primary btn-sm mr-2">
          Edit
        </button>
        <button @click="removeClient(row)" class="btn btn-danger btn-sm">
          Remove
        </button>
      </template>
    </AIVTable>

    <!-- Client Modal -->
    <AIVDialog
      :visible="$modal.state.clientModal"
      :title="modalTitle"
      :show-footer="true"
      :custom-class="'client-modal'"
      @update:visible="$modal.close('clientModal')"
    >
      <!-- Edit Mode -->
      <div v-if="modal.mode === 'edit'" class="client-edit-form">
        <div class="form-group mb-3">
          <label for="clientName" class="form-label">Client Name</label>
          <input
            id="clientName"
            v-model="modal.formData.name"
            type="text"
            class="form-control"
            placeholder="Enter client name"
          />
        </div>
        <div class="form-group mb-3">
          <label for="clientTaxId" class="form-label">Tax ID</label>
          <input
            id="clientTaxId"
            v-model="modal.formData.tax_id"
            type="text"
            class="form-control"
            placeholder="Enter tax ID"
          />
        </div>
        <div class="form-group mb-3">
          <label for="clientDirector" class="form-label">Director</label>
          <AIVSelect
            id="clientDirector"
            :options="directorOptions"
            :value="modal.formData.director_id"
            placeholder="Select a director"
            @input="modal.formData.director_id = $event"
          />
        </div>
        <div class="form-group mb-3">
          <label for="clientInsuranceProviders" class="form-label">Insurance Providers</label>
          <AIVSelect
            id="clientInsuranceProviders"
            :options="insuranceProviderOptions"
            :value="modal.formData.insurance_provider_ids"
            :multiSelect="true"
            placeholder="Select insurance providers"
            @input="modal.formData.insurance_provider_ids = $event"
          />
        </div>
        
        <!-- Google Drive Configuration -->
        <div class="form-group mb-3">
          <div class="form-check mb-2">
            <input
              id="useDirectorDriveEdit"
              v-model="modal.formData.useDirectorDrive"
              type="checkbox"
              class="check-input"
            />
            <label for="useDirectorDriveEdit" class="form-check-label">
              Use Director Google Drive
            </label>
          </div>
          <div v-if="!modal.formData.useDirectorDrive" class="mt-2">
            <label for="customDriveEdit" class="form-label">Custom Google Drive URL</label>
            <input
              id="customDriveEdit"
              v-model="modal.formData.custom_drive"
              type="text"
              class="form-control"
              placeholder="Enter custom Google Drive folder URL"
            />
          </div>
        </div>
      </div>

      <!-- Create Mode -->
      <div v-if="modal.mode === 'create'" class="client-create-form">
        <!-- Toggle between Select Existing and Create New -->
        <div class="form-group mb-4">
          <div class="btn-group w-100" role="group">
            <button 
              type="button" 
              :class="['btn', modal.createMode === 'select' ? 'btn-primary' : 'btn-outline-primary']"
              @click="modal.createMode = 'select'"
            >
              Select Existing Client
            </button>
            <button 
              type="button" 
              :class="['btn', modal.createMode === 'new' ? 'btn-primary' : 'btn-outline-primary']"
              @click="modal.createMode = 'new'"
            >
              Create New Client
            </button>
          </div>
        </div>

        <!-- Select Existing Client Mode -->
        <div v-if="modal.createMode === 'select'">
          <div class="form-group mb-3">
            <label for="existingClient" class="form-label">Select Client</label>
            <AIVSelect
              id="existingClient"
              :options="availableClients"
              :value="modal.formData.selectedClientId"
              placeholder="Choose a client to add to this site"
              @input="modal.formData.selectedClientId = $event"
            />
          </div>
          <div v-if="availableClients.length === 0" class="alert alert-info">
            All available clients are already assigned to this site. You can create a new client instead.
          </div>
        </div>

        <!-- Create New Client Mode -->
        <div v-if="modal.createMode === 'new'">
          <div class="form-group mb-3">
            <label for="newClientName" class="form-label">Client Name</label>
            <input
              id="newClientName"
              v-model="modal.formData.name"
              type="text"
              class="form-control"
              placeholder="Enter client name"
            />
          </div>
          <div class="form-group mb-3">
            <label for="newClientTaxId" class="form-label">Tax ID</label>
            <input
              id="newClientTaxId"
              v-model="modal.formData.tax_id"
              type="text"
              class="form-control"
              placeholder="Enter tax ID"
            />
          </div>
          <div class="form-group mb-3">
            <label for="newClientDirector" class="form-label">Director</label>
            <AIVSelect
              id="newClientDirector"
              :options="directorOptions"
              :value="modal.formData.director_id"
              placeholder="Select a director"
              @input="modal.formData.director_id = $event"
            />
          </div>
          <div class="form-group mb-3">
            <label for="newClientInsuranceProviders" class="form-label">Insurance Providers</label>
            <AIVSelect
              id="newClientInsuranceProviders"
              :options="insuranceProviderOptions"
              :value="modal.formData.insurance_provider_ids"
              :multiSelect="true"
              placeholder="Select insurance providers"
              @input="modal.formData.insurance_provider_ids = $event"
            />
          </div>
          
          <!-- Google Drive Configuration -->
          <div class="form-group mb-3">
            <div class="form-check mb-2">
              <input
                id="useDirectorDriveNew"
                v-model="modal.formData.useDirectorDrive"
                type="checkbox"
                class="check-input"
              />
              <label for="useDirectorDriveNew" class="form-check-label">
                Use Director Google Drive
              </label>
            </div>
            <div v-if="!modal.formData.useDirectorDrive" class="mt-2">
              <label for="customDriveNew" class="form-label">Custom Google Drive URL</label>
              <input
                id="customDriveNew"
                v-model="modal.formData.custom_drive"
                type="text"
                class="form-control"
                placeholder="Enter custom Google Drive folder URL"
              />
            </div>
          </div>
        </div>
      </div>

      <!-- Remove Mode -->
      <div v-if="modal.mode === 'remove'" class="client-remove-confirm">
        <p class="text-center mb-4">
          Are you sure you want to remove the client 
          <strong>"{{ modal.selectedClient?.name }}"</strong> from this site?
        </p>
        <p class="text-muted text-center">This action cannot be undone.</p>
      </div>

      <!-- Modal Footer -->
      <template #footer>
        <div class="d-flex justify-content-around">
          <button 
            @click="closeModal" 
            class="btn btn-secondary"
          >
            Cancel
          </button>
          <button 
            v-if="modal.mode === 'edit'"
            @click="saveClient" 
            class="btn btn-primary ml-2"
            :disabled="modal.loading"
          >
            {{ modal.loading ? 'Saving...' : 'Save' }}
          </button>
          <button 
            v-if="modal.mode === 'create'"
            @click="handleCreateSubmit" 
            class="btn btn-success ml-2"
            :disabled="modal.loading || (modal.createMode === 'select' && !modal.formData.selectedClientId) || (modal.createMode === 'new' && (!modal.formData.name || !modal.formData.tax_id))"
          >
            {{ modal.loading ? 'Adding...' : 'Add Client' }}
          </button>
          <button 
            v-if="modal.mode === 'remove'"
            @click="confirmRemoval" 
            class="btn btn-danger ml-2"
            :disabled="modal.loading"
          >
            {{ modal.loading ? 'Removing...' : 'Remove' }}
          </button>
        </div>
      </template>
    </AIVDialog>

  </div>
</template>

<script>
import SitesAPIService from "@/serviceHandlers/SitesAPIservice";
import AIVTable from "@/components/AIVTable.vue";
import AIVSelect from "@/components/AIVSelect.vue";
import SearchBox from "@/components/SearchBox.vue";
import DirectorsAPIService from "@/serviceHandlers/DirectorsAPIservice";
import ClientsAPIService from "@/serviceHandlers/ClientsAPIservice";
import InsuranceProvidersAPIService from "@/serviceHandlers/InsuranceProviersAPIservice";

const sitesAPIService = new SitesAPIService()
const directorAPIService = new DirectorsAPIService()
const clientsAPIService = new ClientsAPIService()
const insuranceProvidersAPIService = new InsuranceProvidersAPIService()

export default {
  name: "AvailityPage",
  components: {
    AIVTable,
    AIVSelect,
    SearchBox
  },
  data(){
    return {
      searchTerm: '',
      siteData: null,
      directors: [],
      allClients: [],
      insuranceProviders: [],
      clientColumns: [
        { key: 'name', label: 'Name' },
        { key: 'tax_id', label: 'Tin' },
        { key: 'director.name', label: 'Director' },
        { key: 'insurance_providers', label: 'Insurance Providers', component: 'insurance_providers' },
        { key: 'drive_location', label: 'Drive Location', component: 'drive_location' },
        { key: 'dates', label: 'Created / Updated', component: 'dates' },
        { 
          key: 'actions', 
          label: 'Actions', 
          sortable: false,
          component: 'div'
        }
      ],
      modal: {
        mode: null, // 'edit', 'remove', or 'create'
        selectedClient: null,
        loading: false,
        createMode: 'select', // 'select' or 'new'
        formData: {
          name: '',
          tax_id: '',
          director_id: null,
          selectedClientId: null,
          insurance_provider_ids: [],
          useDirectorDrive: true,
          custom_drive: ''
        }
      }
    }
  },
  computed: {
    filteredClients() {
      if (!this.siteData || !this.siteData.clients || !this.searchTerm) {
        return this.siteData?.clients || [];
      }
      
      const searchLower = this.searchTerm.toLowerCase();
      return this.siteData.clients.filter(client => {
        const matchesName = client.name?.toLowerCase().includes(searchLower);
        const matchesTaxId = client.tax_id?.toLowerCase().includes(searchLower);
        const matchesDirector = client.director?.name?.toLowerCase().includes(searchLower);
        const matchesInsuranceProvider = client.insurance_providers?.some(provider => 
          provider.canonical_name?.toLowerCase().includes(searchLower) || 
          provider.name?.toLowerCase().includes(searchLower)
        );
        
        return matchesName || matchesTaxId || matchesDirector || matchesInsuranceProvider;
      });
    },
    modalTitle() {
      if (this.modal.mode === 'edit') {
        return 'Edit Client';
      } else if (this.modal.mode === 'remove') {
        return 'Remove Client';
      } else if (this.modal.mode === 'create') {
        return 'Add Client to Site';
      }
      return '';
    },
    directorOptions() {
      return this.directors.map(director => ({
        id: director.id,
        name: director.name
      }));
    },
    insuranceProviderOptions() {
      return this.insuranceProviders.map(provider => ({
        id: provider.id,
        name: provider.canonical_name
      }));
    },
    availableClients() {
      if (!this.allClients || !this.siteData?.clients) {
        return [];
      }
      // Filter out clients already assigned to this site
      const assignedClientIds = this.siteData.clients.map(client => client.id);
      return this.allClients
        .filter(client => !assignedClientIds.includes(client.id))
        .map(client => ({
          id: client.id,
          name: `${client.name} (${client.tax_id})`
        }));
    }
  },
  async mounted(){
    console.clear()
    await this.getAvailityData()
    await this.getDirectors()
    await this.getAllClients()
    await this.getInsuranceProviders()
  },
  methods: {
    async getAvailityData(){
      try {
        this.siteData = await sitesAPIService.getSiteInfo(1, this.$router)
        console.log(this.siteData)
      } catch (e) {
        console.error(e)
      }
    },
    async getDirectors(){
      try {
        this.directors = await directorAPIService.getDirectors(this.$router)
      } catch (e) {
        console.error(e)
      }
    },
    async getAllClients(){
      try {
        this.allClients = await clientsAPIService.getClients(this.$router)
      } catch (e) {
        console.error(e)
      }
    },
    async getInsuranceProviders(){
      try {
        this.insuranceProviders = await insuranceProvidersAPIService.getInsuranceProviders(this.$router)
      } catch (e) {
        console.error(e)
      }
    },
    editClient(client) {
      console.log(client)
      this.modal.selectedClient = client;
      this.modal.mode = 'edit';
      this.modal.formData.name = client.name;
      this.modal.formData.tax_id = client.tax_id;
      this.modal.formData.director_id = client.director_id;
      this.modal.formData.insurance_provider_ids = client.insurance_providers.map(data => data.id);
      this.modal.formData.useDirectorDrive = !client.custom_drive;
      this.modal.formData.custom_drive = client.custom_drive || '';
      this.$modal.open('clientModal');
    },
    removeClient(client) {
      this.modal.selectedClient = client;
      this.modal.mode = 'remove';
      this.$modal.open('clientModal');
    },
    addClientToSite() {
      this.modal.mode = 'create';
      this.modal.createMode = 'select';
      this.modal.selectedClient = null;
      this.modal.formData.name = '';
      this.modal.formData.tax_id = '';
      this.modal.formData.director_id = null;
      this.modal.formData.selectedClientId = null;
      this.modal.formData.insurance_provider_ids = [];
      this.modal.formData.useDirectorDrive = true;
      this.modal.formData.custom_drive = '';
      this.$modal.open('clientModal');
    },
    closeModal() {
      this.$modal.close('clientModal');
      this.modal.mode = null;
      this.modal.selectedClient = null;
      this.modal.loading = false;
      this.modal.createMode = 'select';
      this.modal.formData.name = '';
      this.modal.formData.tax_id = '';
      this.modal.formData.director_id = null;
      this.modal.formData.selectedClientId = null;
      this.modal.formData.insurance_provider_ids = [];
      this.modal.formData.useDirectorDrive = true;
      this.modal.formData.custom_drive = '';
    },
    async saveClient() {
      this.modal.loading = true;
      try {
        const clientData = {
          name: this.modal.formData.name,
          tax_id: this.modal.formData.tax_id,
          director_id: this.modal.formData.director_id,
          insurance_provider_ids: this.modal.formData.insurance_provider_ids,
          custom_drive: this.modal.formData.useDirectorDrive ? null : this.modal.formData.custom_drive
        };
        
        await clientsAPIService.updateClient(
          this.modal.selectedClient.id,
          clientData,
          this.$router
        );
        
        // Refresh the site data to show updated client info
        await this.getAvailityData();
        this.closeModal();
        
        // Success feedback could be added here (toast notification, etc.)
        console.log('Client updated successfully');
      } catch (error) {
        console.error('Error updating client:', error);
        // Error feedback could be added here (toast notification, alert, etc.)
        alert('Failed to update client. Please try again.');
      } finally {
        this.modal.loading = false;
      }
    },
    async handleCreateSubmit() {
      this.modal.loading = true;
      try {
        if (this.modal.createMode === 'select') {
          // Add existing client to site
          if (!this.modal.formData.selectedClientId) {
            alert('Please select a client to add to the site.');
            return;
          }
          
          await sitesAPIService.addClientToSite(
            this.siteData.id,
            this.modal.formData.selectedClientId,
            this.$router
          );
          
        } else if (this.modal.createMode === 'new') {
          // Create new client and add to site
          if (!this.modal.formData.name || !this.modal.formData.tax_id) {
            alert('Please fill in all required fields (Name and Tax ID).');
            return;
          }
          
          const newClientData = {
            name: this.modal.formData.name,
            tax_id: this.modal.formData.tax_id,
            director_id: this.modal.formData.director_id,
            insurance_provider_ids: this.modal.formData.insurance_provider_ids,
            custom_drive: this.modal.formData.useDirectorDrive ? null : this.modal.formData.custom_drive
          };
          
          // First create the client
          const createdClient = await clientsAPIService.createClient(
            newClientData,
            this.$router
          );
          
          // Then add the new client to the site
          await sitesAPIService.addClientToSite(
            this.siteData.id,
            createdClient.client.id,
            this.$router
          );
        }
        
        // Refresh the site data to show the new client
        await this.getAvailityData();
        // Refresh all clients list
        await this.getAllClients();
        this.closeModal();
        
        console.log('Client successfully added to site');
      } catch (error) {
        console.error('Error adding client to site:', error);
        alert('Failed to add client to site. Please try again.');
      } finally {
        this.modal.loading = false;
      }
    },
    async confirmRemoval() {
      this.modal.loading = true;
      try {
        await sitesAPIService.removeClientFromSite(
          this.siteData.id,
          this.modal.selectedClient.id,
          this.$router
        );
        
        // Refresh the site data to remove the client from the list
        await this.getAvailityData();
        // Refresh all clients list so removed client appears in available list again
        await this.getAllClients();
        this.closeModal();
        
        console.log('Client successfully removed from site');
      } catch (error) {
        console.error('Error removing client from site:', error);
        alert('Failed to remove client from site. Please try again.');
      } finally {
        this.modal.loading = false;
      }
    },
    formatDate(dateString) {
      if (!dateString) return 'N/A';
      const date = new Date(dateString);
      return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric'
      });
    }
  }
};
</script>

<style scoped>
.dates-column {
  font-size: 0.875rem;
}

.created-date {
  margin-bottom: 2px;
}

.updated-date {
  color: #6c757d;
  font-size: 0.8rem;
}

.insurance-providers-column {
  font-size: 0.875rem;
}

.insurance-provider-badge {
  display: inline;
  font-size: 0.8rem;
  color: #495057;
}

.drive-location-column {
  font-size: 0.875rem;
}

.custom-drive a {
  text-decoration: none;
  word-break: break-all;
}

.custom-drive a:hover {
  text-decoration: underline;
}

.director-drive {
  font-style: italic;
}

.check-input {
  opacity: 1 !important;
  position: relative !important;
  visibility: visible !important;
  background-color: white;
  border-color: #000;
}

.check-input:checked {
  background-color: #000;
  border-color: #000;
}

.check-input:focus {
  border-color: #000;
  box-shadow: 0 0 0 0.2rem rgba(0, 0, 0, 0.25);
}
</style>
